import SharedValidation from './SharedValidation.js';
import { defineRule } from 'vee-validate';

defineRule('require-look-up', (value, { hasAtLeastOneLookUpValue, rowData }) => {
    if (hasAtLeastOneLookUpValue(rowData)) return true;
    return `Must include one of: ISBN, title, author, catalog number, or description`;
});


import { max, email, min_value } from '@vee-validate/rules';
export default {
    mixins: [SharedValidation],
    data() {
        return {
            courseRequestHeaderConfiguration: {
                type: 'courserequestsheader',
                label: 'Course Requests Header',
                importModes: [
                    {value:'add', text:'Add New (Skip when course request already present)'},
                    {value:'update', text:'Update (Skip when course request not present, update when found)'},
                    {value:'addAndUpdate', text:'Add and Update'}
                ],
                sampleNotes: [
                    '"Campuses and terms match on abbreviation."',
                    '"Departments and courses match on name."',
                    '"The course must be associated with the deparment"',
                    '"Course requests will match their details based on matching term, department, course, and section."',
                    '"Instructor matches on email, if no match it creates a new instructor from the name and email."',
                    '"Location matches on Name field in Store Setup under the Contact Information tab."'
                ],
                importNotes: () => { return this.importNotes},
                fields: [
                    {
                        key: 'CourseStatus',
                        label: 'Course Status',
                        dataType: 'openCompletedOptions',
                        hasErrorCalculator: value => {
                            return !this.matchesExistingObject(value, false);
                        },
                        rules: 'not_one_of:-1',
                        samples: ['"Open"', '"Completed"', '"Open"']
                    },
                    {
                        key: 'Term',
                        label: 'Term',
                        selectOptionsFilter: () => {
                            return 'True';
                        },
                        hasErrorCalculator: value => {
                            return !this.matchesExistingObject(value, true);
                        },
                        comparisonProperty: 'name',
                        rules: 'min_value:0|required',
                        dataType: 'terms',
                        samples: ['"2022CMTU"', '"2022GTAC"', '"2018AMTU"']
                    },
                    {
                        key: 'Department',
                        label: 'Department',
                        hasErrorCalculator: value => {
                            return !this.matchesExistingObject(value, true);
                        },
                        selectOptionsFilter: (row) => {
                            return row.TermId > 0 ? row.TermId : -1;
                        },
                        dataType: 'courseDepartmentsImport',
                        rules: 'min_value:0|required',
                        samples: ['"AI"', '"BIO"', '"CHEM"']
                    },
                    {
                        key: 'Course',
                        label: 'Course',
                        selectOptionsFilter: (row) => {
                                return row.DepartmentId > 0 ? row.DepartmentId : -1;
                        },
                        hasErrorCalculator: (value, row) => {
                            // A course request always targets an existing course that belongs to
                            // the selected department, regardless of import mode. value is the
                            // resolved CourseId; an unmatched course is -1, which
                            // matchesExistingObject(.., true) treats as invalid (it also covers
                            // the empty/required case).
                            return !this.matchesExistingObject(value, true) || !this.isValidCourseForDepartment(value, row);
                        },
                        dataType:'courses',          
                        rules: 'min_value:0|required',
                        samples: ['"AI1"', '"Biology"', '"101"']
                    },
                    {
                        key: 'Section',//SectionName in the db, matches to section in the UI
                        label: 'Section',
                        canConvertCase: true,
                        hasErrorCalculator: (value) => {
                            return this.isEmpty(value) || max(value, { length: 80 }) !== true;
                        },
                        hasWarningCalculator: (value, row) => {
                            return this.isDuplicateInImport(row);
                        },
                        rules: 'required|max:80',
                        samples: ['"H"', '"A"', '"B"']
                    },
                    {
                        key: 'InstructorName',
                        label: 'Instructor Name',
                        canConvertCase: true,
                        hasErrorCalculator: (value) => {
                            return this.isEmpty(value) || max(value, { length: 80 }) !== true;
                        },

                        rules: 'required|max:80',
                        samples: [
                            '"John"',
                            '"Benning, Jim"',
                            '"Joe Smith"'
                        ]
                    },
                    {
                        key: 'InstructorEmail',
                        label: 'Instructor Email',
                        hasErrorCalculator: (value) => {
                            return this.isEmpty(value) || email(value) !== true ||  max(value, { length: 255 }) !== true;
                        },

                        rules: 'required|email|max:255',
                        samples: [
                            '"john@myschool.edu"',
                            '"jb@myschool.edu"',
                            '"js@myschool.edu"'
                        ]
                    },
                    {
                        key: 'SectionId',
                        label: 'Section Id',
                        hasErrorCalculator: (value) => {
                            return !this.isEmpty(value) && max(value, { length: 20 }) !== true;
                        },

                        rules: 'max:20',
                        samples: ['"123"', '"450"', '"190"']
                    },
                    {
                        key: 'CourseLevel',
                        label: 'Course Level',
                        hasErrorCalculator: value => {
                            return !this.matchesExistingObject(value);
                        },

                        dataType: 'courseLevels',
                        rules: 'min_value:0',
                        samples: ['1', '2', '1']
                    },
                    {
                        key: 'EstimatedEnrollment',
                        label: 'Estimated Enrollment',
                        hasErrorCalculator: (value) => {
                            return !this.isNonNegativeInteger(value);
                        },
                        convertToNumber: true,
                        rules: 'min_value:0|integer',
                        samples: ['80', '85', '90']
                    },

                    {
                        key: 'ActualEnrollment',
                        label: 'Actual Enrollment',
                        hasErrorCalculator: (value) => {
                            return !this.isNonNegativeInteger(value);
                        },
                        convertToNumber: true,
                        rules: 'min_value:0|integer',
                        samples: ['85', '90', '80']
                    },
                    {
                        key: 'WebComment',
                        label: 'Web Comment',
                        hasErrorCalculator: (value) => {
                            return max(value, { length:500 }) !== true;
                        },
                        rules: 'max:500',
                        samples: [
                            '"Web Comment1"',
                            '"Web Comment2"',
                            '"Web Comment3"'
                        ]
                    },
                    {
                        key: 'FacultyComment',
                        label: 'Faculty Comment',
                        hasErrorCalculator: (value) => {
                            return max(value, { length:500 }) !== true;
                        },

                        rules: 'max:500',
                        samples: [
                            '"Faculty Comment1"',
                            '"Faculty Comment2"',
                            '"Faculty Comment3"'
                        ]
                    },
                    {
                        key: 'ShelfTagComment',
                        label: 'Shelf Tag Comment',
                        hasErrorCalculator: (value) => {
                            return max(value, { length:1000 }) !== true;
                        },

                        rules: 'max:1000',
                        samples: [
                            '"Tag Comment1"',
                            '"Tag Comment2"',
                            '"Tag Comment3"'
                        ]
                    },
                    {
                        key: 'PrintShelfTag',
                        label: 'Print Shelf Tag',
                        hasWarningCalculator: value => {
                            return !this.matchesExistingObject(value);
                        },
                        dataType: 'yesNoOptions',
                        warningDefault: true,
                        rules: 'min_value:0',
                        samples: ['"Yes"', '"No"', '"Yes"']
                    },
                    {
                        key: 'Location',
                        label: 'Location',
                        hasErrorCalculator: value => {
                            return (value == '' ||
                                value == undefined ||
                                min_value(value, { min: 0 }) !== true);
                        },
                        rules: 'required|min_value:0',
                        samples: ['"North Campus"', '"City Campus"'],
                        dataType: 'locations'
                    },                   
                ],
                canRunNoParse: true,
                cronJobTypeId: 30
            }
        };
    },
    watch: {
        sameAsAddress: {
            handler: function() {
                this.syncSameAsAddress();
            }
        },
    },

    computed:{
        importNotes(){
            let notes = []
            if(this.newInstructors.length > 0){
                notes.push(`New instructor(s) will be created for the following instructor email(s) not found: ${this.newInstructors.join(', ')}`  )
            }
            if(this.newTermDepartmentAssociations.length > 0){
                notes.push(`New department/term association(s) will be created with 100% allocation percentage for the following: ${this.newTermDepartmentAssociations.join(', ')}`  )
            }
            if(this.loadedData.some(row => this.isDuplicateInImport(row))){
                notes.push('Duplicate Course Request entries were found in the import file.  These will not be processed.')
            }

            return notes;
        },

        newInstructors(){
            return [...new Set(this.loadedData.filter(x => 
                !this.selectListOptions.instructors.some(s => s.email === x.InstructorEmail))
                .map(x => x.InstructorEmail))];
        },

        newTermDepartmentAssociations(){
            return this.loadedData.filter(x => 
                !this.selectListOptions.terms.some(t => t.courseDepartmentIds.includes(x.DepartmentId)))
                .map(x => x.Department + '/' + x.Term);

        },
    },
    
    methods: {
        isDuplicateInImport(row) {
            // Course requests match on term, department, course, and section (section case-insensitive).
            const section = row.Section?.toLowerCase();
            if (!section) return false;
            const matches = this.loadedData.filter(x =>
                x.TermId === row.TermId
                && x.DepartmentId === row.DepartmentId
                && x.CourseId === row.CourseId
                && x.Section?.toLowerCase() === section);
            // The last occurrence is the one imported; earlier duplicates warn.
            return matches.length > 1 && matches[matches.length - 1] !== row;
        },
        isValidCourseForDepartment(course, row) {
            if(this.isEmpty(course) ||  row.DepartmentId < 1){
                return true;
            }
            return this.selectListOptions.courses.find(x => x.value == course)?.filter == row.DepartmentId
        },

        hasAtLeastOneLookUpValue(row){
            return !(this.isEmpty(row.Isbn)
                && this.isEmpty(row.CatalogNumber)
                && this.isEmpty(row.Title)
                && this.isEmpty(row.Description));
        }

    }
};
